import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UserHelperService } from '@helpers/user-helper.service';
import { ChannelI } from '@interfaces/channel.interface';
import { ChatI, HttpChatI, InternalChatI, ListI } from '@interfaces/chat.interface';
import { ContactI } from '@interfaces/contact.interface';
import { ChatStatusT } from './../types/chat.type';
import { TeamI } from '@interfaces/team.interface';
import { WhatsappOmniService } from '@services/whatsapp-omni/whatsapp-omni.service';
import { StrategyE } from '@enums/strategy.enum';
import { TeamsI } from '@interfaces/teams.interface';
import { AclAbilityService } from '@services/acl/acl.ability';
import { PermissionType, ProfileLevel, ResourceNames } from '@enums/acl/resources.enum';
import { ChatService } from '@modules/chat/chat.service';
import { UserSocketLoginI } from '@interfaces/user.interface';
import { KanbanCategoryI } from '@interfaces/kanban-category.interface';
import { DefaultKanbanCategoryNamesE } from '@enums/default-kanban-category-names.enum';
import { ChatFilterI } from '@interfaces/chat-filter.interface';
import { MessageI } from '@interfaces/message.interface';
import { ChatStatusE } from '@enums/chat-status.enum';
import { MapChatsT } from '@omnitypes/chat-store.type';
import { PropertyChatE } from '@enums/chat-store.enum';

@Injectable({
  providedIn: 'root',
})
export class ChatHelperService {
  constructor(
    private readonly userHelperService: UserHelperService,
    private readonly whatsappOmniService: WhatsappOmniService,
    private readonly aclService: AclAbilityService,
    private readonly chatService: ChatService
  ) {}

  public get currentUser() {
    return this.userHelperService.getSocketLoginData();
  }

  public startTranscription(message: MessageI) {
    return this.chatService.startTranscription(message);
  }

  public generateInternalChat(internalAgents: ListI[], channel: ChannelI): InternalChatI {
    return {
      customerId: this.currentUser ? this.currentUser.customerId : '',
      status: 'internal',
      channel,
      internalAgents,
      transfers: [],
      messages: [],
    } as InternalChatI;
  }

  public generateChat(
    contact: ContactI,
    channel: ChannelI,
    status: ChatStatusT,
    team?: TeamI,
    phoneNumber: string = ''
  ): HttpChatI {
    return {
      customerId: this.currentUser ? this.currentUser.customerId : '',
      status,
      channel:
        channel ||
        ({
          _id: '62f51da26c9abb4eee406604',
          type: 'whatsapp',
          channel: {
            name: 'instancia whatsapp',
            cellphone: '551142700584',
            startFlow: '62e3ef6e0f310fba79838e74',
            endFlow: '62e3ef6e0f310fba79838e74',
            token: 'M_ozIYsJbML5ZkglV0',
            instance: '11107',
            provider: 'megaapi',
          },
          status: 'active',
          customerId: '62e3e63f6dda97d36f667166',
          createdAt: '2022-08-11T15:17:54.574Z',
          updatedAt: '2022-08-11T15:17:54.574Z',
          icon: '',
          class: '',
          __v: 0,
        } as ChannelI) ||
        '62f51da26c9abb4eee406604',
      contact,
      agent: this.currentUser ? this.currentUser._id : '',
      team: team ? team : undefined,
      transfers: [],
      messages: [],
      internalAgents: [],
      phoneNumber,
    };
  }

  public generateWhatsappOficialChat(
    contact: ContactI,
    channel: ChannelI,
    status: ChatStatusT,
    team?: TeamI,
    phoneNumber: string = ''
  ): HttpChatI {
    return {
      customerId: this.currentUser ? this.currentUser.customerId : '',
      status,
      channel:
        channel ||
        ({
          _id: '636b0637923f941f2c7988ce',
          type: 'whatsapp-oficial',
          channel: {
            name: 'OmniSmart',
            cellphone: '08008906000',
            bspKey: 'Q0LV2ce7v4iritsRTB20ibucAK',
            provider: '360dialog',
          },
          status: 'active',
          customerId: '62e3e63f6dda97d36f667166',
          workerflow: '62f16445bd729ffd30b69bd0',
          createdAt: '2022-08-11T15:17:54.574Z',
          updatedAt: '2022-08-11T15:17:54.574Z',
          __v: 0,
          icon: 'whatsapp-oficial',
          class: 'surf',
        } as ChannelI),
      contact,
      agent: this.currentUser ? this.currentUser._id : '',
      team: team ? team : undefined,
      transfers: [],
      messages: [],
      internalAgents: [],
      phoneNumber,
    };
  }

  public generateInternalAgents(user: ListI): ListI[] {
    return [
      {
        _id: user._id,
        name: user.name,
        avatar: user.avatar,
        activityStatus: user.activityStatus || 'offline',
      },
      {
        _id: this.currentUser?._id || '',
        name: this.currentUser?.name || '',
        avatar: this.currentUser?.avatar || '',
        activityStatus: 'online',
      },
    ];
  }

  public getContactNameByChatStatus(status: string, chat: ChatI): string {
    let name: string = 'Contato Excluído';

    switch (status) {
      case 'internal':
      case 'archived':
        if (!chat.internalAgents.length) {
          return chat.contact?.name ?? name;
        }
        const names: string[] = [];
        chat.internalAgents.map((agent) => {
          if (agent?._id !== this.currentUser?._id) {
            names.push(agent.name);
          }
        });

        chat?.mentions?.map((mention) => {
          if (mention?.agent?.name?.length && mention?.agent?._id !== this.currentUser?._id) {
            names.push(mention?.agent?.name);
          }
        });

        return `${names.join(', ')}`;
      default:
        if (!chat.contact) return name;
        return chat.contact.name || chat.contact.telephone || chat.contact.email || 'Contato Excluído';
    }
  }

  public getChatCategory(
    userCopy: UserSocketLoginI,
    category: KanbanCategoryI,
    page: number,
    limit: number,
    searchText = '',
    customSort = {}
  ) {
    switch (category.name) {
      case DefaultKanbanCategoryNamesE.FINISHED:
        return this.chatService.getAllFinishedChatsOnlyLast(
          userCopy,
          page,
          limit,
          searchText,
          { teamOnly: this.isUserAdminLevel() },
          customSort
        );
      case DefaultKanbanCategoryNamesE.ARCHIVED:
        return this.chatService.getAllArchivedChats(
          userCopy.team,
          page,
          limit,
          searchText,
          { teamOnly: this.isUserAdminLevel(), fromKanban: true },
          customSort
        );
      case DefaultKanbanCategoryNamesE.WORKFLOW:
        return this.chatService.getAllFlowChats(userCopy.team, page, limit, searchText, {} as ChatFilterI, customSort);
      case DefaultKanbanCategoryNamesE.RECENT:
        return this.chatService.getAllMyRecentChatsForKanban(
          category.team as string,
          page,
          limit,
          searchText,
          customSort
        );
      case DefaultKanbanCategoryNamesE.ATTENDANCE:
        return this.chatService.getAllMyChatsInAttendanceForKanban(
          category.team as string,
          page,
          limit,
          searchText,
          customSort
        );
    }
    return;
  }

  private isUserAdminLevel(): boolean {
    return this.userHelperService.currentLevel() >= ProfileLevel.HIGH;
  }

  public async setChatActionStatus(contact: ContactI, channel: ChannelI, option: string) {
    try {
      if (!channel.instanceKey) return { error: true };
      const telephone = contact.telephone ? contact.telephone : (contact?.telephones as any)[0];

      return await firstValueFrom(
        this.whatsappOmniService.setChatActionStatus({
          jid: telephone,
          instance: channel.instanceKey,
          option,
        })
      );
    } catch (e) {
      console.log('falha ao buscar ações de chat do contato', e);
      return {
        error: true,
      };
    }
  }

  public getContactPhoto(status: string, chat: ChatI): string | undefined {
    let photo;
    switch (status) {
      case 'internal':
        if (!chat.internalAgents) return photo;
        chat.internalAgents.map((agent) => {
          if (agent?._id !== this.currentUser?._id) {
            photo = agent.photo;
          }
        });
        return photo;

      default:
        if (!chat.contact) return photo;
        if (chat.contact.avatar) return chat.contact.avatar;
        return undefined;
    }
  }

  public getSelfInternalAgentFromChat(chat: ChatI) {
    return chat.internalAgents?.find((agent) => agent?._id === this.currentUser?._id);
  }

  public getContactId(chat: ChatI, chatStatus: ChatStatusT): string | void {
    switch (chatStatus) {
      case 'internal':
        const tmpUser = chat.internalAgents?.find((agent) => agent?._id !== this.currentUser?._id);
        return tmpUser?._id;
      default:
        return chat.contact?._id;
    }
  }

  public isUserMentionedChat(chat: ChatI): boolean {
    const user = this.userHelperService.getUserAttributes()! as any;

    if (chat.agent?._id !== user['custom:userId']) {
      if (chat.mentions && chat.mentions.length > 0) {
        return !!chat.mentions.find((mention) => mention.agent?._id === user['custom:userId'] && mention.count > 0);
      }
    }

    return false;
  }

  public isInQueueChat(chat: ChatI) {
    if (chat.status !== ChatStatusE.QUEUE) {
      return false;
    }
    return chat.team?.configuration?.strategy !== StrategyE.ALL_AGENTS && !chat.agent;
  }

  public isSupervisor(chat: ChatI) {
    const user = this.userHelperService.getUserAttributes()! as any;
    if (chat?.team) {
      const { supervisors, isDefault } = chat?.team as TeamsI;
      const isSupervisorOfTeam = supervisors ? supervisors.includes(user['custom:userId']) : false;
      return isSupervisorOfTeam || (isDefault && this.userHelperService.isCurrentUserMidLevel());
    }
    return false;
  }

  public isAttendance(chat: ChatI): boolean {
    return chat.status === 'attendance';
  }

  public cannotTransferChat(chat: ChatI): boolean {
    const { status } = chat;
    return (
      status === 'finished' ||
      (!this.userHelperService.isAdminOrHigher() && this.isInQueueChat(chat)) ||
      (!this.userHelperService.isAdminOrHigher() && !this.isSupervisor(chat) && !this.isAttendance(chat)) ||
      !this.aclService.can(PermissionType.TRANSFER, ResourceNames.ATTENDANCE__CHATS) ||
      this.isUserMentionedChat(chat)
    );
  }

  public isInternalByChannel(chat: ChatI): boolean {
    return chat.channel.type === 'internal';
  }

  public cannotFinishAttendance(chat: ChatI): boolean {
    const { status } = chat;

    return (
      (!this.userHelperService.isAdminOrHigher() && this.isInQueueChat(chat)) ||
      (!this.userHelperService.isAdminOrHigher() && (status === 'queue' || status === ChatStatusE.WAITING)) ||
      status === 'internal' ||
      status === 'finished' ||
      this.isInternalByChannel(chat) ||
      (this.isUserMentionedChat(chat) && !this.isSupervisor(chat)) ||
      !this.aclService.can(PermissionType.FINISH, ResourceNames.ATTENDANCE__CHATS)
    );
  }

  public canArchiveChat(chat: ChatI): boolean {
    if (
      chat.status !== ChatStatusE.ATTENDANCE &&
      chat.status !== ChatStatusE.QUEUE &&
      chat.status !== ChatStatusE.WAITING
    ) {
      return false;
    }

    return !this.userHelperService.isCurrentUserLowLevel() || chat.status === ChatStatusE.ATTENDANCE;
  }

  public canUnarchiveChat(chat: ChatI): boolean {
    if (chat.status !== ChatStatusE.ARCHIVED) {
      return false;
    }

    const currentUserId = this.userHelperService.getUserAttributes()['custom:userId'];

    return !this.userHelperService.isCurrentUserLowLevel() || !chat.agent || chat.agent?._id === currentUserId;
  }

  public mapStoreChats(): MapChatsT[] {
    return [
      { property: PropertyChatE.CHATS, chatType: 'attendances', status: ChatStatusE.ATTENDANCE },
      { property: PropertyChatE.QUEUE, chatType: 'attendances', status: ChatStatusE.QUEUE },
      { property: PropertyChatE.WAITING, chatType: 'attendances', status: ChatStatusE.WAITING },
      { property: PropertyChatE.IN_QUEUE, chatType: 'in_queue', status: ChatStatusE.QUEUE },
      { property: PropertyChatE.DELAYED, chatType: 'delayed', status: ChatStatusE.DELAYED },
      { property: PropertyChatE.ARCHIVED, chatType: 'archived', status: ChatStatusE.ARCHIVED },
      { property: PropertyChatE.FINISHED, chatType: 'finished', status: ChatStatusE.FINISHED },
      { property: PropertyChatE.FLOW, chatType: 'start_flow', status: ChatStatusE.STARTFLOW },
      { property: PropertyChatE.FLOW, chatType: 'start_flow', status: ChatStatusE.NEW },
      { property: PropertyChatE.FLOW, chatType: 'start_flow', status: ChatStatusE.TRANSFER },
      { property: PropertyChatE.API, chatType: 'api', status: ChatStatusE.API },
      { property: PropertyChatE.WORKFLOW, chatType: 'workflow', status: ChatStatusE.WORKFLOW },
      { property: PropertyChatE.INTERNAL, chatType: 'internal', status: ChatStatusE.INTERNAL },
    ];
  }
}
